<template>
  <div class="mt-6 rounded-md bg-surface-container py-4 sm:p-6">
    <div class="flex h-12 items-center justify-between gap-3 px-4 sm:pr-0">
      <div class="flex min-w-0 items-center gap-4">
        <Checkbox
          v-if="downloadableFiles.length > 0"
          :model-value="allSelected"
          :indeterminate="someSelected && !allSelected"
          :aria-label="$t('content.file-list.select-all')"
          @update:model-value="toggleSelectAll"
        />
        <span class="truncate text-sm font-bold text-on-surface">
          {{ headerLabel }}
        </span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <div class="hidden sm:flex">
          <Button
            :label="
              selectedIds.size > 0
                ? $t('content.file-list.download-selected-count', {
                    count: selectedIds.size,
                  })
                : $t('content.file-list.download-selected')
            "
            :size="ButtonSize.MD"
            :variant="ButtonVariant.OUTLINED"
            :color="ButtonColor.SECONDARY"
            :disabled="selectedIds.size === 0 || isDownloading"
            @click="downloadSelected"
          />
        </div>
        <Button
          :label="$t('content.file-list.download-all')"
          :size="ButtonSize.MD"
          :disabled="downloadableFiles.length === 0 || isDownloading"
          @click="downloadAll"
        />
      </div>
    </div>

    <div
      class="mt-2 flex h-9 items-center justify-between border-b border-line text-xs font-bold text-on-surface-variant sm:mt-3 sm:h-auto sm:py-3"
    >
      <span class="pl-12">{{ $t('content.file-list.file-name') }}</span>
      <span class="hidden pr-2 sm:inline">
        {{ $t('content.file-list.action') }}
      </span>
    </div>

    <FileListRow
      v-for="file in files"
      :key="file.id"
      class="cursor-pointer border-b border-line"
      :file-name="getDisplayFileName(file)"
      :extension="getFileNameExtension(getSourceFileName(file))"
      :size-bytes="file.fileSize"
      :uploaded-at="file.fileUploadedAt"
      :custom-preview-url="file.customPreviewUrl"
      :preview-url="file.previewUrl"
      :file-url="file.fileUrl"
      :resolve-image-url="getThumbnailImageUrl"
      @click="previewedFile = file"
    >
      <template #leading>
        <div class="flex shrink-0" @click.stop>
          <Checkbox
            v-if="file.showDownloadButton"
            :model-value="selectedIds.has(file.id)"
            :aria-label="
              $t('content.file-list.select-file', {
                name: getDisplayFileName(file),
              })
            "
            @update:model-value="toggleSelected(file.id)"
          />
          <div v-else class="w-4"></div>
        </div>
      </template>
      <template #actions>
        <div @click.stop>
          <div class="hidden items-center gap-1 sm:flex">
            <Button
              :label="$t('content.file-list.view')"
              :size="ButtonSize.XS"
              :variant="ButtonVariant.TEXT"
              :color="ButtonColor.SECONDARY"
              @click="previewedFile = file"
            />
            <Button
              v-if="file.showDownloadButton"
              :label="$t('common.download')"
              :size="ButtonSize.XS"
              @click="downloadContentFile(file)"
            />
          </div>
          <div class="flex items-center sm:hidden">
            <span class="sr-only">
              <Button
                :label="$t('content.file-list.view')"
                :size="ButtonSize.XS"
                :variant="ButtonVariant.TEXT"
                :color="ButtonColor.SECONDARY"
                @click="previewedFile = file"
              />
            </span>
            <Button
              v-if="file.showDownloadButton"
              :label="$t('common.download')"
              :size="ButtonSize.SM"
              :variant="ButtonVariant.OUTLINED"
              compact
              @click="downloadContentFile(file)"
            />
          </div>
        </div>
      </template>
    </FileListRow>

    <div
      v-if="selectedIds.size > 0"
      class="sticky bottom-[var(--iframe-bottom-inset,0px)] z-40 -mb-4 flex items-center justify-between gap-md rounded-b-md border-t border-line bg-surface-container px-lg py-2xl text-on-surface-primary sm:hidden"
    >
      <div class="flex min-w-0 items-center">
        <span class="whitespace-nowrap text-lg font-semibold">
          {{
            $t('content.file-list.selected-short', { count: selectedIds.size })
          }}
        </span>
        <Button
          class="underline"
          :label="$t('content.file-list.clear-selection')"
          :size="ButtonSize.LG"
          :variant="ButtonVariant.TEXT"
          compact
          @click="selectedIds = new Set()"
        />
      </div>
      <Button
        :label="
          $t('content.file-list.download-selected-count', {
            count: selectedIds.size,
          })
        "
        :size="ButtonSize.MD"
        :variant="ButtonVariant.OUTLINED"
        :color="ButtonColor.SECONDARY"
        :disabled="isDownloading"
        compact
        @click="downloadSelected"
      />
    </div>
  </div>

  <FilePreviewModal
    v-if="previewedFile"
    :file="previewedFile"
    :order-in-store-url="getOrderInStoreUrl(previewedFile)"
    @close="previewedFile = null"
    @download="previewedFile && downloadContentFile(previewedFile)"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import imageProxyBuilder, { ResizeType } from '@bitpatty/imgproxy-url-builder';
import { useRuntimeConfig } from '#imports';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  FileListRow,
  buildDownloadFileName,
  getFileNameExtension,
  getFileNameFromUrl,
  AlertType,
  useToast,
} from '@exfluential/ui';
import { Checkbox } from '~/components/inputs/checkbox';
import { FilePreviewModal } from './file-preview-modal';
import { downloadFile } from '~/utils/file';
import { useLoader } from '~/composables/use-loader';
import {
  createContentMetric,
  ContentMetricType,
  createContentLaunchMetric,
  LaunchMetricType,
} from '~/api/metrics';
import { apiDownloadContentFilesZip, ContentDto } from '~/api/contents';
import { HubDto } from '~/api/hubs';
import { useAccountStore } from '~/store/account';
import { AccountType } from '~/api/accounts';

const toast = useToast();

type ContentFile = NonNullable<ContentDto['files']>[number];

type Props = {
  hub: HubDto;
  content: ContentDto;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  complete: [];
}>();

const { t } = useI18n();
const config = useRuntimeConfig();
const { currentAccount } = storeToRefs(useAccountStore());

const files = computed<ContentFile[]>(() => props.content.files ?? []);

const downloadableFiles = computed(() =>
  files.value.filter((file) => file.showDownloadButton),
);

const selectedIds = ref(new Set<number>());
const previewedFile = ref<ContentFile | null>(null);
const isDownloading = ref(false);

const getSourceFileName = (file: ContentFile): string =>
  file.fileName || getFileNameFromUrl(file.fileUrl);

const getDisplayFileName = (file: ContentFile): string =>
  file.title || getSourceFileName(file);

const getDownloadFileName = (file: ContentFile): string =>
  buildDownloadFileName(getDisplayFileName(file), getSourceFileName(file));

const getThumbnailImageUrl = (url: string): string =>
  imageProxyBuilder()
    .resize({ type: ResizeType.FIT, width: 320, height: 180 })
    .format('webp')
    .build({
      baseUrl: config.public.imgProxyBaseUrl || '',
      path: url,
      addExtension: true,
    });

const allSelected = computed(
  () =>
    downloadableFiles.value.length > 0 &&
    selectedIds.value.size === downloadableFiles.value.length,
);
const someSelected = computed(() => selectedIds.value.size > 0);

const headerLabel = computed(() =>
  someSelected.value
    ? t('content.file-list.selected-count', {
        selected: selectedIds.value.size,
        count: downloadableFiles.value.length,
      })
    : t(
        'content.file-list.files-count',
        { count: files.value.length },
        files.value.length,
      ),
);

const toggleSelected = (fileId: number) => {
  const next = new Set(selectedIds.value);
  if (next.has(fileId)) {
    next.delete(fileId);
  } else {
    next.add(fileId);
  }
  selectedIds.value = next;
};

const toggleSelectAll = () => {
  selectedIds.value = allSelected.value
    ? new Set()
    : new Set(downloadableFiles.value.map((file) => file.id));
};

const getOrderInStoreUrl = (file: ContentFile): string | null => {
  const shouldShow =
    file.isOrderableInStore ??
    (file.availableForPrint || file.availableForStoreProduct);
  return shouldShow ? `/store?file_id=${file.id}` : null;
};

const launchCompletionSent = ref(false);

const trackLaunchCompletion = async () => {
  if (
    !props.hub.isLaunchEnabled ||
    currentAccount.value?.type !== AccountType.STANDARD ||
    props.content.launchData.completedAt ||
    launchCompletionSent.value
  ) {
    return;
  }
  launchCompletionSent.value = true;
  await createContentLaunchMetric(
    props.hub.slug,
    props.content.slug,
    LaunchMetricType.COMPLETED,
  );
  emit('complete');
};

const downloadContentFile = async (
  file: ContentFile,
  options?: { deferCompletion?: boolean },
): Promise<boolean> => {
  const fileName = getDownloadFileName(file);

  createContentMetric(
    props.hub.slug,
    props.content.slug,
    ContentMetricType.FILE_DOWNLOAD,
  );

  const isDownloaded = await downloadFile(file.fileUrl, fileName);
  if (!isDownloaded) return false;

  toast.add({
    type: AlertType.SUCCESS,
    title: t('content.file-list.download-success', {
      name: getDisplayFileName(file),
    }),
  });

  if (!options?.deferCompletion) {
    await trackLaunchCompletion();
  }
  return true;
};

const downloadAsZip = async (toDownload: ContentFile[]): Promise<boolean> => {
  let isDownloaded = false;

  await useLoader({
    action: async () => {
      const blob = await apiDownloadContentFilesZip(
        props.hub.slug,
        props.content.slug,
        toDownload.map((file) => file.id),
      );
      const { saveAs } = await import('file-saver');
      saveAs(blob, `${props.content.slug}-files.zip`);
      isDownloaded = true;
    },
    onError: () => {
      toast.add({
        type: AlertType.ERROR,
        title: t('content.file-list.download-error'),
      });
    },
  });

  if (isDownloaded) {
    toast.add({
      type: AlertType.SUCCESS,
      title: t('content.file-list.download-zip-success', {
        count: toDownload.length,
      }),
    });
  }
  return isDownloaded;
};

const downloadMany = async (toDownload: ContentFile[]): Promise<boolean> => {
  if (toDownload.length === 0 || isDownloading.value) return false;
  isDownloading.value = true;
  let anyDownloaded = false;
  try {
    anyDownloaded =
      toDownload.length === 1
        ? await downloadContentFile(toDownload[0]!, { deferCompletion: true })
        : await downloadAsZip(toDownload);
  } finally {
    isDownloading.value = false;
  }
  if (anyDownloaded) {
    await trackLaunchCompletion();
  }
  return anyDownloaded;
};

const downloadSelected = async () => {
  const toDownload = downloadableFiles.value.filter((file) =>
    selectedIds.value.has(file.id),
  );
  const isDownloaded = await downloadMany(toDownload);
  if (isDownloaded) {
    const downloadedIds = new Set(toDownload.map((file) => file.id));
    selectedIds.value = new Set(
      [...selectedIds.value].filter((id) => !downloadedIds.has(id)),
    );
  }
};

const downloadAll = async () => {
  await downloadMany(downloadableFiles.value);
};
</script>
